import {
  ArrowDownIcon,
  ArrowsUpDownIcon,
  ArrowUpIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import React from 'react';

import { NONE_OPTION_KEY, noneOption } from '@shared/lib/ui/render';

import { AnimatePresence, ChevronRotate, FadeIn, FadeSlideIn, StaggerItem } from './Animations';
import { Button } from './Button';
import { Checkbox } from './Checkbox';
import { cn } from './cn';
import { getFoldedColumnKeys } from './columnFolding';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './DropdownMenu';
import { InfiniteScrollFooter } from './InfiniteScrollFooter';
import { Loader } from './Loader';
import { SearchInput } from './SearchInput';
import { Tooltip } from './Tooltip';
import { raisedPanelClassName } from './WidgetCard';

export interface DataListColumnFilterOption {
  key: string;
  text: string;
  onRenderIcon?: () => React.ReactNode;
}

export interface DataListColumnFilter {
  value?: string;
  options: DataListColumnFilterOption[];
  placeholder?: string;
  multivalueColumnFilteringEnabled?: boolean;
  noneOptionLabel?: string;
}

export interface DataListColumn<T = any> {
  key: string;
  name?: string;
  width?: number | string;
  minWidth?: number;
  maxWidth?: number;
  renderHeader?: (item: T) => React.ReactNode;
  renderCell?: (item: T) => React.ReactNode;
  className?: string;
  /** Extra classes applied only to the column-title header cell (not the body/group cells). */
  headerClassName?: string;
  /** API sort key; when set (and `sortProps` is provided) the column header sorts on click. */
  sortKey?: string;
  /** Per-column filter dropdown in the header; requires `filterProps`. */
  filter?: DataListColumnFilter;
  /** Stick the column to the left edge when `horizontalScroll` is on (flat lists only). */
  pinned?: boolean;
  /** Opt out of drag-resizing for this column when `resizableColumns` is on (default: resizable). */
  resizable?: boolean;
  /**
   * Fold this column out of the row once the measured list width drops under this many pixels.
   * Its cell content then renders in a wrapping chip row under the `foldAnchor` column's content,
   * so trailing columns (row actions) keep their place instead of being clipped.
   */
  foldBelow?: number;
  /**
   * The column that receives folded columns' content. It becomes the row's flexible column
   * (no fixed width, minimum 0) once any column in the same set declares `foldBelow`. Without the
   * flag, the first column without a fixed `width` is used.
   */
  foldAnchor?: boolean;
}

export interface DataListGroup<THeader = any> {
  key: string;
  name: string;
  headerData: THeader;
  startIndex: number;
  count: number;
  isCollapsed?: boolean;
  /** Backward-compatible data bag for legacy IGroup consumers. */
  data?: any;
  [key: string]: any;
}

export interface ActionProps {
  title: string;
  onClick: () => void;
  icon?: React.ReactNode;
}

export interface DataListSortColumn {
  key: string;
  descending: boolean;
}

export interface DataListSortProps {
  sortColumns: DataListSortColumn[];
  onChange: (columns: DataListSortColumn[]) => void;
  /** Shown as an info tooltip in the action bar (e.g. how to multi-sort). */
  hint?: string;
}

export interface DataListFilterProps {
  onChange: (columnKey: string, value: string | undefined) => void;
  onClearAll?: () => void;
  /** When off (default), setting a filter on one column clears the others. */
  multicolumnFilteringEnabled?: boolean;
}

export interface DataListSelectionProps {
  selectedCount: number;
  onSelectAll: () => void;
  allSelected: boolean;
  disabled?: boolean;
  actions?: React.ReactNode;
}

export interface DataListProps<TItem = any, THeader = any> {
  items: TItem[];
  headerColumns?: DataListColumn<THeader>[];
  itemColumns: DataListColumn<TItem>[];
  groups?: DataListGroup<THeader>[];
  loading?: boolean;
  onToggleGroup?: (groupKey: string) => void;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  /**
   * Gates loading on scroll. `true`: the next page loads as the end of the list comes into view. `false`: the list
   * is complete and no footer shows. Left out: the footer shows a Load more button only, as it did before #8630.
   */
  hasNextPage?: boolean;
  /** The last next-page fetch failed: the footer says so and offers a retry instead of loading again on scroll. */
  isLoadMoreError?: boolean;
  searchProps?: {
    placeholder?: string;
    ariaLabel?: string;
    value?: string;
    onChange?: (value: string) => void;
  };
  isSearching?: boolean;
  actionProps?: ActionProps;
  customActions?: React.ReactNode;
  forceShowActionBar?: boolean;
  emptyState?: React.ReactNode;
  error?: boolean;
  errorState?: React.ReactNode;
  className?: string;
  /** Accessible name for the `role="table"` region when column headers are shown. */
  tableAriaLabel?: string;
  /**
   * Always render the expand/collapse toggle on group headers, even when a group currently has no loaded items.
   * Use this for lazy-loaded groups whose items are fetched on expand (so `count` is 0 until then).
   */
  alwaysShowToggle?: boolean;
  /** Column-header sorting; columns opt in via `sortKey`. Fully controlled — arrows render from `sortColumns`. */
  sortProps?: DataListSortProps;
  /** Per-column filter dropdowns; columns opt in via `filter`. */
  filterProps?: DataListFilterProps;
  /** Select-all toolbar with bulk actions; row checkboxes are rendered by the consumer as a regular column. */
  selectionProps?: DataListSelectionProps;
  /**
   * Wrap the rows in a scroll container so wide tables scroll horizontally (and vertically past
   * ~70vh). Works for both flat and grouped lists; `pinned` columns (sticky left edge) apply to flat lists only.
   */
  horizontalScroll?: boolean;
  /** Drag-to-resize handles on column headers; columns opt out via `resizable: false`. Flat lists only. */
  resizableColumns?: boolean;
}

const ColumnFilter = ({
  value,
  onChange,
  options,
  multivalueColumnFilteringEnabled,
  noneOptionLabel,
}: DataListColumnFilter & { onChange: (value: string | undefined) => void }) => {
  const [searchText, setSearchText] = React.useState('');

  const filteredOptions = React.useMemo(
    () => (searchText ? options.filter(({ text }) => text.toLowerCase().includes(searchText.toLowerCase())) : options),
    [options, searchText]
  );

  const selectedValues = React.useMemo<Set<string>>(() => {
    if (!value) return new Set();
    if (multivalueColumnFilteringEnabled) return new Set(value.split(','));
    return new Set([value]);
  }, [value, multivalueColumnFilteringEnabled]);

  const onMenuItemClick = (key: string, ev?: Event | React.MouseEvent) => {
    if (multivalueColumnFilteringEnabled) {
      ev?.preventDefault(); // Keep menu open for multi-select.
      if (key === NONE_OPTION_KEY) {
        onChange(undefined);
        return;
      }
      const newSelection = new Set(selectedValues);
      if (newSelection.has(key)) {
        newSelection.delete(key);
      } else {
        newSelection.add(key);
      }
      onChange(newSelection.size > 0 ? Array.from(newSelection).join(',') : undefined);
    } else {
      onChange(key === NONE_OPTION_KEY ? undefined : key);
    }
  };

  const hasValue = selectedValues.size > 0;

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (!open) setSearchText('');
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'inline-flex cursor-pointer items-center justify-center border-none bg-transparent p-0',
            hasValue ? 'text-info-600' : 'text-text-secondary'
          )}
          aria-label="Filter column"
          onClick={(e) => e.stopPropagation()}
        >
          <ChevronDownIcon className="h-3 w-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-80" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-surface p-3 pb-2">
          <SearchInput value={searchText} onChange={setSearchText} placeholder="Search..." />
        </div>
        <div className="max-h-75 overflow-y-auto">
          <DropdownMenuItem onClick={(e) => onMenuItemClick(noneOption.key, e)}>
            {noneOptionLabel ? <span className="italic">{noneOptionLabel}</span> : <span>{noneOption.text}</span>}
          </DropdownMenuItem>
          {filteredOptions.map((opt) => (
            <DropdownMenuItem key={opt.key} onClick={(e) => onMenuItemClick(opt.key, e)}>
              {multivalueColumnFilteringEnabled && (
                <Checkbox checked={selectedValues.has(opt.key)} onChange={() => {}} />
              )}
              {!multivalueColumnFilteringEnabled && selectedValues.has(opt.key) && (
                <span className="mr-1 text-info-500">&#10003;</span>
              )}
              {opt.onRenderIcon?.()}
              <span>{opt.text}</span>
            </DropdownMenuItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// ─── Column folding ──────────────────────────────────────────────────────────

const getFoldAnchorKey = (columns: DataListColumn<any>[]): string | undefined =>
  (columns.find((col) => col.foldAnchor) ?? columns.find((col) => col.width === undefined) ?? columns[0])?.key;

/**
 * The columns to render at the measured list width: folded columns are removed and the anchor
 * renders their cells as a wrapping chip row under its own content. The anchor also becomes the
 * row's flexible column (no fixed width, minimum 0) so it takes the freed width and shrinks first,
 * keeping trailing fixed-width columns (row actions) in place. Untouched when nothing folds.
 */
const foldColumns = <T,>(columns: DataListColumn<T>[], listWidth: number | undefined): DataListColumn<T>[] => {
  if (!columns.some((col) => col.foldBelow !== undefined)) return columns;
  const anchorKey = getFoldAnchorKey(columns);
  const foldedKeys = getFoldedColumnKeys(columns, listWidth);
  const folded = columns.filter((col) => foldedKeys.has(col.key));
  const renderFolded = (render: (col: DataListColumn<T>) => React.ReactNode) =>
    folded.length ? (
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1.5" data-test="data-list-folded-cells">
        {folded.map((col) => (
          // `contents` keeps the flex gap between rendered chips only: a folded cell whose
          // renderer returns nothing (an absent pill) leaves no empty slot behind.
          <div key={col.key} className="contents" data-test={`data-list-folded-${col.key}`}>
            {render(col)}
          </div>
        ))}
      </div>
    ) : null;

  return columns.flatMap((col) => {
    if (foldedKeys.has(col.key)) return [];
    if (col.key !== anchorKey) return [col];
    return [
      {
        ...col,
        width: undefined,
        minWidth: 0,
        maxWidth: undefined,
        renderCell: (item: T) => (
          <div className="min-w-0 flex-1" data-test="data-list-fold-anchor">
            {col.renderCell?.(item)}
            {renderFolded((foldedCol) => foldedCol.renderCell?.(item))}
          </div>
        ),
        // Same nullish fallback as GroupHeader: a `renderHeader` that returns null still falls
        // back to `renderCell`.
        renderHeader: (item: T) => (
          <div className="min-w-0 flex-1" data-test="data-list-fold-anchor">
            {col.renderHeader?.(item) ?? col.renderCell?.(item)}
            {renderFolded((foldedCol) => foldedCol.renderHeader?.(item) ?? foldedCol.renderCell?.(item))}
          </div>
        ),
      },
    ];
  });
};

const columnFlexStyle = (col: DataListColumn<any>): React.CSSProperties => ({
  width: col.width,
  minWidth: col.minWidth,
  maxWidth: col.maxWidth,
  flex: col.width ? `0 0 ${typeof col.width === 'number' ? `${col.width}px` : col.width}` : '1 1 0%',
});

const columnPixelWidth = (col: DataListColumn<any>): number =>
  typeof col.width === 'number' ? col.width : col.minWidth ?? 150;

/** Sticky-left offsets for pinned columns (flat lists with horizontalScroll only). */
const usePinnedColumnStyles = (columns: DataListColumn<any>[], enabled: boolean) =>
  React.useMemo(() => {
    const styles: Record<string, { left: number; isLast: boolean }> = {};
    if (!enabled) return styles;
    let left = 0;
    let lastPinnedKey: string | undefined;
    for (const col of columns) {
      if (!col.pinned) continue;
      styles[col.key] = { left, isLast: false };
      left += columnPixelWidth(col);
      lastPinnedKey = col.key;
    }
    if (lastPinnedKey) styles[lastPinnedKey].isLast = true;
    return styles;
  }, [columns, enabled]);

// Pinned cells are transparent at rest so they are indistinguishable from their siblings. Once
// the table is scrolled horizontally they switch to an opaque background (plus the edge divider)
// to occlude passing content — the horizontal-scroll surface is a solid `bg-surface` panel, so
// the switched-on background is the exact same color and stays invisible.
const pinnedCellClassName = (isLast: boolean, scrolled: boolean, bgClassName = 'bg-surface') =>
  cn(
    'sticky z-2 transition-colors duration-150',
    scrolled && bgClassName,
    scrolled &&
      isLast &&
      "after:content-[''] after:absolute after:top-0 after:right-0 after:bottom-0 after:border-r-2 after:border-double after:border-border"
  );

/** Implements the same click cycle as the legacy mlops DataTable: asc → desc → none; shift-click multi-sorts. */
const computeNextSortColumns = (
  sortColumns: DataListSortColumn[],
  apiSortKey: string,
  isMultiSort: boolean
): DataListSortColumn[] => {
  const currentSort = sortColumns.find((s) => s.key === apiSortKey);
  if (isMultiSort) {
    if (currentSort) {
      if (currentSort.descending) return sortColumns.filter((s) => s.key !== apiSortKey);
      return sortColumns.map((s) => (s.key === apiSortKey ? { ...s, descending: true } : s));
    }
    return [...sortColumns, { key: apiSortKey, descending: false }];
  }
  if (currentSort && sortColumns.length === 1) {
    if (currentSort.descending) return [];
    return [{ key: apiSortKey, descending: true }];
  }
  return [{ key: apiSortKey, descending: false }];
};

function GroupHeader<THeader>({
  group,
  columns,
  onToggle,
  alwaysShowToggle,
  minWidth,
}: {
  group: DataListGroup<THeader>;
  columns: DataListColumn<THeader>[];
  onToggle?: () => void;
  alwaysShowToggle?: boolean;
  minWidth?: number;
}) {
  const showToggle = alwaysShowToggle || group.count > 0;
  return (
    <FadeIn
      className="flex items-center border-b border-border bg-transparent transition-colors duration-200 hover:bg-surface-hover/60"
      role="row"
      transition={{ duration: 0.2 }}
      style={minWidth ? { minWidth } : undefined}
    >
      {showToggle && (
        <button
          onClick={onToggle}
          className="flex h-11 w-9 shrink-0 items-center justify-center bg-transparent border-none cursor-pointer text-text-muted hover:text-text-primary transition-colors duration-200"
          aria-label={group.isCollapsed ? 'Expand group' : 'Collapse group'}
        >
          <ChevronRotate open={!group.isCollapsed}>
            <ChevronRightIcon className="h-4 w-4" />
          </ChevronRotate>
        </button>
      )}
      {!showToggle && <div className="w-9 shrink-0" />}
      {columns.map((col) => (
        <div
          key={col.key}
          className={cn('flex min-w-0 items-center overflow-hidden px-3 py-3 text-sm text-text-primary', col.className)}
          style={columnFlexStyle(col)}
          role="cell"
        >
          {col.renderHeader?.(group.headerData) ?? col.renderCell?.(group.headerData)}
        </div>
      ))}
    </FadeIn>
  );
}

function ItemRow<TItem>({
  item,
  columns,
  index,
  indented,
  minWidth,
  pinnedStyles,
  pinnedActive,
}: {
  item: TItem;
  columns: DataListColumn<TItem>[];
  index?: number;
  indented?: boolean;
  minWidth?: number;
  pinnedStyles: Record<string, { left: number; isLast: boolean }>;
  pinnedActive?: boolean;
}) {
  return (
    <StaggerItem
      className={cn(
        'flex items-center border-b border-border/50 bg-transparent transition-colors duration-200',
        'hover:bg-surface-hover/60',
        indented && 'pl-9'
      )}
      role="row"
      index={index || 0}
      direction="left"
      style={minWidth ? { minWidth } : undefined}
    >
      {columns.map((col) => {
        const pin = pinnedStyles[col.key];
        return (
          <div
            key={col.key}
            className={cn(
              // min-w-0 + overflow-hidden keep cell content contained (flex cells do not clip by default)
              'flex min-w-0 items-center overflow-hidden px-3 py-2.5 text-sm text-text-primary',
              pin && pinnedCellClassName(pin.isLast, Boolean(pinnedActive)),
              col.className
            )}
            style={{ ...columnFlexStyle(col), ...(pin ? { left: pin.left } : {}) }}
            role="cell"
          >
            {col.renderCell?.(item)}
          </div>
        );
      })}
    </StaggerItem>
  );
}

export function DataList<TItem, THeader>({
  items,
  headerColumns = [],
  itemColumns,
  groups,
  loading,
  onToggleGroup,
  onLoadMore,
  isLoadingMore,
  hasNextPage,
  isLoadMoreError,
  searchProps,
  isSearching,
  actionProps,
  customActions,
  forceShowActionBar,
  emptyState,
  error,
  errorState,
  className,
  tableAriaLabel,
  alwaysShowToggle,
  sortProps,
  filterProps,
  selectionProps,
  horizontalScroll,
  resizableColumns,
}: DataListProps<TItem, THeader>) {
  const hasItems = items.length > 0 || (groups && groups.length > 0);
  // Grouped lists page by group (a model, a feature set), so that is the count the end line reports.
  const pagedCount = groups ? groups.length : items.length;
  const footer = onLoadMore ? (
    <InfiniteScrollFooter
      hasNextPage={hasNextPage}
      isFetchingNextPage={isLoadingMore}
      isError={isLoadMoreError}
      onLoadMore={onLoadMore}
      itemCount={pagedCount}
    />
  ) : null;
  const showActionBar = hasItems || forceShowActionBar;
  const actionBarRef = React.useRef<HTMLDivElement>(null);
  const [actionBarHeight, setActionBarHeight] = React.useState(0);

  React.useEffect(() => {
    if (actionBarRef.current) {
      setActionBarHeight(actionBarRef.current.offsetHeight);
    }
  }, [showActionBar]);

  // Resizing only applies to flat lists (grouped rows render header columns instead).
  const resizingEnabled = Boolean(resizableColumns && !groups);
  const [widthOverrides, setWidthOverrides] = React.useState<Record<string, number>>({});
  const isResizingRef = React.useRef(false);
  // True once the container is scrolled horizontally — pinned cells then need an opaque background.
  const [pinnedActive, setPinnedActive] = React.useState(false);

  const effectiveItemColumns = React.useMemo(
    () =>
      resizingEnabled
        ? itemColumns.map((col) =>
            widthOverrides[col.key] !== undefined ? { ...col, width: widthOverrides[col.key] } : col
          )
        : itemColumns,
    [resizingEnabled, itemColumns, widthOverrides]
  );

  // Column folding: one observer on the table measures the width every row folds against. The
  // table mounts only once there is something to list, so a callback ref (not a mount-time
  // effect) attaches the observer whenever it (re)appears.
  const foldingEnabled = [...itemColumns, ...headerColumns].some((col) => col.foldBelow !== undefined);
  const foldObserverRef = React.useRef<ResizeObserver>();
  const [listWidth, setListWidth] = React.useState<number>();
  const tableRef = React.useCallback(
    (element: HTMLDivElement | null) => {
      foldObserverRef.current?.disconnect();
      foldObserverRef.current = undefined;
      if (!foldingEnabled || !element) return;
      setListWidth(element.getBoundingClientRect().width);
      if (typeof ResizeObserver === 'undefined') return;
      const observer = new ResizeObserver(([entry]) => {
        if (entry) setListWidth(entry.contentRect.width);
      });
      observer.observe(element);
      foldObserverRef.current = observer;
    },
    [foldingEnabled]
  );
  const foldedItemColumns = foldColumns(effectiveItemColumns, listWidth);
  const foldedHeaderColumns = foldColumns(headerColumns, listWidth);

  const columns = groups ? foldedHeaderColumns : foldedItemColumns;
  // Pinning only makes sense on flat lists inside the horizontal scroll container.
  const pinningEnabled = Boolean(horizontalScroll && !groups);
  const pinnedStyles = usePinnedColumnStyles(foldedItemColumns, pinningEnabled);
  const rowMinWidth = horizontalScroll
    ? columns.reduce((acc, col) => acc + columnPixelWidth(col), groups ? 36 : 0)
    : undefined;

  const sortColumns = sortProps?.sortColumns ?? [];

  const handleHeaderSortClick = (col: DataListColumn<any>, event: React.MouseEvent) => {
    // A click fires on the header right after a resize drag ends — swallow it.
    if (isResizingRef.current) return;
    if (!col.sortKey || !sortProps) return;
    sortProps.onChange(computeNextSortColumns(sortColumns, col.sortKey, event.shiftKey));
  };

  const startColumnResize = (event: React.MouseEvent, col: DataListColumn<any>) => {
    event.preventDefault();
    event.stopPropagation();
    isResizingRef.current = true;
    const startX = event.clientX;
    const headerCell = (event.currentTarget as HTMLElement).parentElement;
    const startWidth = widthOverrides[col.key] ?? headerCell?.offsetWidth ?? columnPixelWidth(col);
    const minWidth = col.minWidth ?? 60;
    const onMouseMove = (moveEvent: MouseEvent) => {
      const nextWidth = Math.max(minWidth, startWidth + (moveEvent.clientX - startX));
      setWidthOverrides((prev) => ({ ...prev, [col.key]: nextWidth }));
    };
    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      // Reset after the trailing click event has been dispatched so the sort handler ignores it.
      window.setTimeout(() => {
        isResizingRef.current = false;
      }, 0);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  // When multicolumn filtering is off, setting a filter on one column clears the others.
  const handleColumnFilterChange = (columnKey: string, value: string | undefined) => {
    if (!filterProps) return;
    if (!filterProps.multicolumnFilteringEnabled && value !== undefined) {
      for (const otherCol of columns) {
        if (otherCol.key !== columnKey && otherCol.filter?.value) {
          filterProps.onChange(otherCol.key, undefined);
        }
      }
    }
    filterProps.onChange(columnKey, value);
  };

  // When there are no items and no action bar, render empty/error state without the card wrapper
  if (!loading && !hasItems && !showActionBar) {
    if (error && errorState) {
      return (
        <FadeSlideIn className={cn('flex flex-1 flex-col items-center justify-center', className)}>
          {errorState}
        </FadeSlideIn>
      );
    }
    if (emptyState) {
      return (
        <FadeSlideIn className={cn('flex flex-1 flex-col items-center justify-center', className)}>
          {emptyState}
        </FadeSlideIn>
      );
    }
  }

  const hasColumnNames = columns.some((col) => col.name || (col.sortKey && sortProps) || col.filter);
  const hasActiveColumnFilter = columns.some((col) => col.filter?.value);
  // The search term is a filter too (just a different kind), so it counts toward "active filters".
  const hasActiveSearch = Boolean(searchProps?.value);
  const hasActiveFilter = hasActiveColumnFilter || hasActiveSearch;
  // Clear both column filters and the search term.
  const clearAllFilters = () => {
    filterProps?.onClearAll?.();
    if (hasActiveSearch) {
      searchProps?.onChange?.('');
    }
  };

  const headerRow = hasColumnNames && (
    <div
      className={cn(
        'sticky z-9 flex items-center border-b border-border-strong',
        // Solid header on solid (horizontal-scroll) surfaces so pinned header cells can match it exactly.
        horizontalScroll ? 'bg-border dark:bg-surface-raised' : 'bg-border dark:bg-border/60',
        groups && 'pl-9'
      )}
      style={{ top: horizontalScroll ? 0 : actionBarHeight, ...(rowMinWidth ? { minWidth: rowMinWidth } : {}) }}
      role="row"
    >
      {columns.map((col) => {
        const sortable = Boolean(col.sortKey && sortProps);
        const currentSort = col.sortKey ? sortColumns.find((s) => s.key === col.sortKey) : undefined;
        const sortIndex = col.sortKey ? sortColumns.findIndex((s) => s.key === col.sortKey) : -1;
        const isMultiSort = sortColumns.length > 1 && sortIndex >= 0;
        const isHighlighted = Boolean(currentSort || col.filter?.value);
        const pin = pinningEnabled ? pinnedStyles[col.key] : undefined;
        return (
          <div
            key={col.key}
            className={cn(
              'relative min-w-0 overflow-hidden px-3 py-2.5 type-table-header text-text-secondary',
              sortable && 'cursor-pointer select-none',
              pin && pinnedCellClassName(pin.isLast, pinnedActive, 'bg-border dark:bg-surface-raised'),
              // (pinned header bg matches the solid horizontal-scroll header row above)
              col.className,
              col.headerClassName
            )}
            style={{ ...columnFlexStyle(col), ...(pin ? { left: pin.left } : {}) }}
            role="columnheader"
            {...(sortable
              ? {
                  tabIndex: 0,
                  'aria-sort': currentSort ? (currentSort.descending ? 'descending' : 'ascending') : 'none',
                  onClick: (event: React.MouseEvent) => handleHeaderSortClick(col, event),
                  onKeyDown: (event: React.KeyboardEvent) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleHeaderSortClick(col, event as unknown as React.MouseEvent);
                    }
                  },
                }
              : {})}
          >
            <span className={cn('flex items-center gap-1', isHighlighted && 'font-bold text-info-600')}>
              {col.name}
              {sortable && (
                <span className="ml-1 flex items-center text-xs">
                  {currentSort ? (
                    <>
                      {currentSort.descending ? (
                        <ArrowDownIcon className="h-3 w-3" />
                      ) : (
                        <ArrowUpIcon className="h-3 w-3" />
                      )}
                      {isMultiSort && sortIndex + 1}
                    </>
                  ) : (
                    <ArrowsUpDownIcon className="h-3 w-3" />
                  )}
                </span>
              )}
              {col.filter && filterProps && (
                <ColumnFilter {...col.filter} onChange={(value) => handleColumnFilterChange(col.key, value)} />
              )}
            </span>
            {resizingEnabled && col.resizable !== false && (
              <div
                role="separator"
                aria-orientation="vertical"
                aria-label={`Resize ${col.name || col.key} column`}
                onMouseDown={(event) => startColumnResize(event, col)}
                onClick={(event) => event.stopPropagation()}
                className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize touch-none select-none bg-info-500/60 opacity-0 hover:opacity-100"
              />
            )}
          </div>
        );
      })}
    </div>
  );

  const rows = groups
    ? groups.map((group) => {
        const groupItems = items.slice(group.startIndex, group.startIndex + group.count);
        return (
          <React.Fragment key={group.key}>
            <GroupHeader
              group={group}
              columns={foldedHeaderColumns}
              onToggle={() => onToggleGroup?.(group.key)}
              alwaysShowToggle={alwaysShowToggle}
              minWidth={rowMinWidth}
            />
            <AnimatePresence>
              {!group.isCollapsed &&
                groupItems.map((item, idx) => (
                  <ItemRow
                    key={`${group.key}-${idx}`}
                    item={item}
                    columns={foldedItemColumns}
                    index={idx}
                    indented
                    minWidth={rowMinWidth}
                    pinnedStyles={pinnedStyles}
                  />
                ))}
            </AnimatePresence>
          </React.Fragment>
        );
      })
    : items.map((item, idx) => (
        <ItemRow
          key={idx}
          item={item}
          columns={foldedItemColumns}
          index={idx}
          minWidth={rowMinWidth}
          pinnedStyles={pinnedStyles}
          pinnedActive={pinnedActive}
        />
      ));

  const table = (
    <div role="table" ref={tableRef} {...(tableAriaLabel ? { 'aria-label': tableAriaLabel } : {})}>
      {headerRow}
      {rows}
    </div>
  );

  return (
    <FadeSlideIn
      className={cn(
        'overflow-hidden rounded-xl',
        // Horizontally scrolling tables use a solid surface: pinned cells must occlude passing
        // content with an opaque background, which could never match a frosted (translucent) panel.
        horizontalScroll ? 'border border-border bg-surface shadow-sm' : raisedPanelClassName,
        className
      )}
    >
      {showActionBar && (
        <div
          ref={actionBarRef}
          className="sticky top-0 z-10 flex flex-wrap items-center gap-3 border-b border-border-strong bg-transparent px-4 py-3"
        >
          {searchProps && (
            <SearchInput
              placeholder={searchProps.placeholder}
              ariaLabel={searchProps.ariaLabel}
              value={searchProps.value}
              onChange={searchProps.onChange}
              className="w-64"
            />
          )}
          {(filterProps?.onClearAll || searchProps?.onChange) &&
            (hasActiveFilter ? (
              <Button variant="outline" size="sm" onClick={clearAllFilters}>
                <XMarkIcon className="h-4 w-4" />
                Clear all filters
              </Button>
            ) : filterProps?.onClearAll ? (
              <span className="flex items-center gap-1 type-body-sm text-text-muted">
                No filters active
                <Tooltip content="Filterable columns show a chevron next to their title — click it to filter column.">
                  <InformationCircleIcon className="h-4 w-4 text-text-muted" />
                </Tooltip>
              </span>
            ) : null)}
          {selectionProps && (
            <div className="flex items-center gap-3">
              <Checkbox
                checked={selectionProps.allSelected}
                label="Select all"
                onChange={selectionProps.onSelectAll}
                disabled={selectionProps.disabled}
              />
              {selectionProps.selectedCount > 0 && (
                <>
                  <span className="type-body-sm text-text-secondary">
                    Selected {selectionProps.selectedCount} item{selectionProps.selectedCount > 1 ? 's' : ''}
                  </span>
                  {selectionProps.actions}
                </>
              )}
            </div>
          )}
          {customActions}
          <div className="flex-1" />
          {sortProps?.hint && (
            <Tooltip content={sortProps.hint}>
              <InformationCircleIcon className="h-4 w-4 text-text-muted" />
            </Tooltip>
          )}
          {actionProps && (
            <Button variant="primary" size="sm" onClick={actionProps.onClick}>
              {actionProps.icon}
              {actionProps.title}
            </Button>
          )}
        </div>
      )}

      {loading && !isSearching ? (
        <Loader label="Loading..." />
      ) : error && errorState && !hasItems ? (
        errorState
      ) : !hasItems ? (
        emptyState
      ) : horizontalScroll ? (
        <div
          className="overflow-auto"
          style={{ maxHeight: 'calc(100vh - 300px)' }}
          onScroll={(event) => setPinnedActive(event.currentTarget.scrollLeft > 0)}
        >
          {table}
          {/* Inside the scrolling box, so reaching the sentinel means scrolling through the rows; sticky so it stays
              in view while the rows scroll sideways. */}
          {footer && <div className="sticky left-0">{footer}</div>}
        </div>
      ) : (
        table
      )}

      {!horizontalScroll && footer}
    </FadeSlideIn>
  );
}
